// THE CLIP RACK — the wall's bottom editor, from Designer A's winning board
// (Oct 9 2026, Paul: "just go do it and be bold. I like this designer's mind").
// The editor is a device rack, not a form: a left-to-right chain of devices
// — TASK · NOTE · CHECKLIST · FINISH — and a device only exists when the
// task has that content, so the chain's content decides the rack's height
// and length. A sentence-task folds the whole rack into one bar. The long
// note keeps a 46ch measure by flowing into newspaper columns that scroll
// horizontally — the same axis the wall itself scrolls.
// Laws kept: open/active/done only; absolute counts ("3 steps left");
// ✦ is the user's own mark; the commit controls are the only bordered
// things in the rack; the wall above never reflows.
import React, { useRef, useState } from 'react';
import { X } from 'lucide-react';
import ActionEditor from './ActionEditor';

const MONO = "ui-monospace, 'SF Mono', SFMono-Regular, Menlo, monospace";

const DeviceLabel = ({ children, right }) => (
  <div className="flex items-baseline gap-2 mb-2 select-none">
    <span className="text-[9.5px] font-bold uppercase themed-text-muted"
      style={{ letterSpacing: '0.14em', fontFamily: MONO }}>{children}</span>
    {right && <span className="text-[9.5px] themed-text-muted" style={{ fontFamily: MONO }}>{right}</span>}
  </div>
);

export default function ClipRack({
  action,
  updateAction,
  setActionStatus,
  toggleActionComplete,
  completeWithReceipt,
  toggleActionPho,
  addChecklistItem,
  setSelectedActionId,
  dock,
  onDockChange,
}) {
  const noteText = (action.notes || '').replace(/<[^>]*>/g, ' ').replace(/&nbsp;/g, ' ').trim();
  const checklist = action.checklist || [];
  // "+ steps" materializes the checklist before its content exists; the
  // NOTE device is always open for writing (Paul, Oct 9 2026: a fold you
  // cannot type into is unusable — the minimum got taller instead)
  const [stepsOpen, setStepsOpen] = useState(false);
  const receiptRef = useRef(null);
  const hasNote = true;
  const hasCheck = checklist.length > 0 || stepsOpen;
  const folded = false;
  // ONE height. A DAW's clip view is a fixed instrument — it never
  // resizes itself between clips (Paul, Oct 9 2026: three heights were
  // jarring note-to-note).
  const height = 380;

  const statusWord = action.completed ? 'done' : (action.status === 'doing' || action.status === 'done' ? 'active' : 'open');
  const cycle = () => {
    if (action.completed) { toggleActionComplete(action.id); return; }
    if (action.status === 'doing' || action.status === 'done') {
      // the honest ritual, in place: the receipt line is right there
      if (receiptRef.current && receiptRef.current.value.trim()) {
        completeWithReceipt(action.id, receiptRef.current.value);
      } else if (receiptRef.current) {
        receiptRef.current.placeholder = 'Write the receipt line first — how did it end?';
        receiptRef.current.focus();
      } else {
        toggleActionComplete(action.id);
      }
      return;
    }
    setActionStatus(action.id, 'doing');
  };
  const close = () => {
    const r = receiptRef.current;
    if (!r || !r.value.trim()) {
      if (r) { r.placeholder = 'Write the receipt line first — how did it end?'; r.focus(); }
      return;
    }
    completeWithReceipt(action.id, r.value);
  };
  const setChecklist = (list) => updateAction(action.id, { checklist: list });
  const stepsLeft = checklist.filter(c => !c.completed).length;

  const taskDevice = (
    <div className="flex-shrink-0 flex flex-col min-w-0 h-full" style={{ width: folded ? undefined : 320, flex: folded ? '1 1 auto' : undefined }}>
      <div className={`flex ${folded ? 'items-center gap-4 h-full' : 'flex-col gap-2.5'}`}>
        <button onClick={cycle}
          className="px-2.5 py-1 rounded-full text-[11px] font-medium border themed-primary-bg text-white border-transparent flex-shrink-0 self-start"
          style={folded ? { alignSelf: 'center' } : undefined}
          title="Click to change: open → active → done">{statusWord}</button>
        <h2
          contentEditable suppressContentEditableWarning
          onBlur={(e) => { const v = e.currentTarget.innerText.trim(); if (v && v !== action.text) updateAction(action.id, { text: v }); }}
          onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); e.currentTarget.blur(); } }}
          className={`font-bold themed-text leading-snug focus:outline-none min-w-0 ${folded ? 'text-[16px] flex-1 truncate' : 'text-[17px]'}`}
          style={action.completed ? { textDecoration: 'line-through', color: 'var(--color-textMuted)' } : undefined}
        >{action.text}</h2>
        <button onClick={() => toggleActionPho(action.id)}
          className="flex items-center gap-1.5 text-[11.5px] bg-transparent border-0 cursor-pointer p-0 flex-shrink-0 self-start"
          style={{ color: action.pho ? 'var(--color-pho)' : 'var(--color-textMuted)', alignSelf: folded ? 'center' : undefined }}
          title="Yours alone — the app never marks it">
          <span style={{ color: 'var(--color-pho)' }}>✦</span>{action.pho ? 'starred for today' : 'star for today'}
        </button>
        {!action.completed && !hasCheck && (
          <button onClick={() => setStepsOpen(true)}
            className="self-start text-[11px] themed-text-muted hover:themed-text bg-transparent border-0 cursor-pointer p-0">+ steps</button>
        )}
        {!folded && (
          <p className="text-[10px] themed-text-muted mt-auto select-none">
            <kbd className="border themed-border rounded px-1 py-0.5 text-[9px]" style={{ fontFamily: MONO }}>esc</kbd>
            {' '}closes the editor — the wall above never moves
          </p>
        )}
      </div>
    </div>
  );

  const noteDevice = hasNote && !action.completed && (
    <div className="flex-1 min-w-0 flex flex-col h-full pl-6" style={{ borderLeft: '1px solid var(--color-borderLight)' }}>
      <DeviceLabel>Note</DeviceLabel>
      {/* The horizontal-columns bet was retired Oct 9 2026 — long notes
          "got stuck" past the window. The note scrolls down, at a reading
          measure, like every page since paper. */}
      <div className="flex-1 min-h-0 overflow-y-auto cursor-text"
        onClick={(e) => {
          // the whole device is the page: clicking empty paper puts the caret in it
          const ed = e.currentTarget.querySelector('.tiptap');
          if (ed && !ed.contains(e.target)) ed.focus();
        }}>
        <div style={{ maxWidth: '62ch' }}>
          <ActionEditor
            key={`rack-${action.id}`}
            content={action.notes || ''}
            onSave={(md) => updateAction(action.id, { notes: md })}
          />
        </div>
      </div>
    </div>
  );

  const checkDevice = hasCheck && !action.completed && (
    <div className="flex-shrink-0 flex flex-col h-full pl-6" style={{ width: 340, borderLeft: '1px solid var(--color-borderLight)' }}>
      <DeviceLabel right={stepsLeft > 0 ? `${stepsLeft} step${stepsLeft === 1 ? '' : 's'} left` : 'all done'}>Checklist</DeviceLabel>
      <div className="flex-1 min-h-0 overflow-y-auto">
        {checklist.map(c => (
          <div key={c.id} className="flex items-start gap-2 py-1 group/rci">
            <button onClick={() => setChecklist(checklist.map(x => x.id === c.id ? { ...x, completed: !x.completed } : x))}
              className="text-[13px] leading-relaxed flex-shrink-0 themed-text-muted hover:themed-text bg-transparent border-0 cursor-pointer p-0"
              title={c.completed ? 'Mark undone' : 'Mark done'}>{c.completed ? '✓' : '○'}</button>
            <span className={`text-[12.5px] leading-relaxed flex-1 ${c.completed ? 'themed-text-muted' : 'themed-text'}`}>{c.text}</span>
            <button onClick={() => setChecklist(checklist.filter(x => x.id !== c.id))}
              className="text-[11px] themed-text-muted opacity-0 group-hover/rci:opacity-100 flex-shrink-0 bg-transparent border-0 cursor-pointer p-0">✕</button>
          </div>
        ))}
        <input
          key={`rci-${action.id}-${checklist.length}`}
          placeholder="Add a step…"
          autoFocus={stepsOpen && checklist.length === 0}
          onKeyDown={(e) => { if (e.key === 'Enter' && e.target.value.trim()) { addChecklistItem(action.id, e.target.value); e.target.value = ''; } }}
          className="w-full text-[12.5px] themed-text bg-transparent border-0 focus:outline-none py-1"
        />
      </div>
    </div>
  );

  // the output stage: a rack ends in its commit controls — the only
  // bordered things here
  const finishDevice = (
    <div className={`flex-shrink-0 flex ${folded ? 'items-center gap-3' : 'flex-col h-full pl-6'}`}
      style={folded ? { marginLeft: 'auto' } : { width: 300, borderLeft: '1px solid var(--color-borderLight)' }}>
      {!folded && <DeviceLabel>Finish</DeviceLabel>}
      {action.completed ? (
        <div className="flex flex-col gap-2">
          {action.resolutionComment && (
            <p className="text-[12.5px] leading-relaxed" style={{ color: 'var(--color-success)' }}>↳ {action.resolutionComment}</p>
          )}
          <button onClick={() => toggleActionComplete(action.id)}
            className="self-start px-3 py-1.5 text-[12px] rounded-lg border themed-border themed-text-muted hover:themed-text bg-transparent cursor-pointer">
            Reopen
          </button>
        </div>
      ) : (
        <>
          <input ref={receiptRef}
            key={`rcpt-${action.id}`}
            placeholder="How did it end? One line, for the record."
            onKeyDown={(e) => { if (e.key === 'Enter') close(); }}
            className={`text-[12.5px] themed-text bg-transparent border themed-border rounded-lg px-3 py-2 focus:outline-none placeholder:themed-text-muted placeholder:opacity-60 ${folded ? '' : 'w-full'}`}
            style={folded ? { width: 300 } : undefined}
          />
          <button onClick={close}
            className={`px-3.5 py-1.5 text-[12.5px] font-semibold rounded-lg cursor-pointer ${folded ? '' : 'self-start mt-2.5'}`}
            style={{
              border: '1px solid color-mix(in srgb, var(--color-success) 55%, transparent)',
              background: 'color-mix(in srgb, var(--color-success) 10%, transparent)',
              color: 'var(--color-success)',
            }}>
            Close <kbd className="text-[9.5px] ml-1" style={{ fontFamily: MONO }}>⏎</kbd>
          </button>
          {!folded && (
            <p className="text-[10px] themed-text-muted leading-relaxed mt-auto">
              Close writes the receipt line and moves the clip to done.
            </p>
          )}
        </>
      )}
    </div>
  );

  return (
    <div className="clip-rack relative flex items-stretch gap-0 px-7 pt-4 pb-5 text-left"
      style={{ height, transition: 'height .2s ease' }}>
      {/* the rack's own corner: dock choice + close */}
      <div className="absolute top-3 right-4 flex items-center gap-2 z-10">
        {onDockChange && (<>
          <button onClick={() => onDockChange('bottom')}
            className="w-6 h-6 rounded border text-[12px] leading-none bg-transparent cursor-pointer"
            style={dock === 'bottom' ? { color: 'var(--color-text)', borderColor: 'var(--color-primary)' } : { color: 'var(--color-textMuted)', borderColor: 'var(--color-border)' }}
            title="Clip view (bottom)">⬓</button>
          <button onClick={() => onDockChange('right')}
            className="w-6 h-6 rounded border text-[12px] leading-none bg-transparent cursor-pointer"
            style={dock === 'right' ? { color: 'var(--color-text)', borderColor: 'var(--color-primary)' } : { color: 'var(--color-textMuted)', borderColor: 'var(--color-border)' }}
            title="Inspector (right)">◨</button>
        </>)}
        <button onClick={() => setSelectedActionId(null)}
          className="p-1 rounded themed-text-muted hover:themed-text bg-transparent border-0 cursor-pointer"
          title="Close (Esc)"><X size={14}/></button>
      </div>
      {taskDevice}
      {noteDevice}
      {checkDevice}
      {finishDevice}
    </div>
  );
}
